import React from 'react';
import {
    Alert,
    Box,
    Button,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Tooltip,
    Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ITenantApiKey } from '@pepperhq/types-sdk';
import { MuiModal } from 'ui/MuiModal';
import { useSnackbar } from 'notistack';

interface ApiKeyCreatedModalProps {
    apiKey: ITenantApiKey<string> | null;
    onClose: () => void;
}

const monoInputProps = { style: { fontFamily: 'monospace', fontSize: '0.85rem' } };

export const ApiKeyCreatedModal: React.FC<ApiKeyCreatedModalProps> = ({ apiKey, onClose }) => {
    const { enqueueSnackbar } = useSnackbar();

    const handleCopyToken = React.useCallback(() => {
        if (!apiKey) {
            return;
        }
        navigator.clipboard.writeText(apiKey.sessionId);
        enqueueSnackbar('Auth token copied', { variant: 'success' });
    }, [apiKey, enqueueSnackbar]);

    const handleCopyApplicationId = React.useCallback(() => {
        if (!apiKey) {
            return;
        }
        navigator.clipboard.writeText(apiKey.applicationId);
        enqueueSnackbar('Application ID copied', { variant: 'success' });
    }, [apiKey, enqueueSnackbar]);

    return (
        <MuiModal
            open={!!apiKey}
            onClose={onClose}
            title="API Key Created"
            disableBackdropClick
            paperMaxWidth={560}
        >
            <Box>
                <Alert severity="warning" sx={{ mt: 1, mb: 2.5 }}>
                    <strong>Copy the auth token now.</strong> It will not be shown again once you close this
                    dialog.
                </Alert>
                <Box mb={2}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                    >
                        Auth Token
                    </Typography>
                    <OutlinedInput
                        fullWidth
                        readOnly
                        value={apiKey?.sessionId ?? ''}
                        size="small"
                        inputProps={monoInputProps}
                        endAdornment={
                            <InputAdornment position="end">
                                <Tooltip title="Copy auth token">
                                    <IconButton size="small" onClick={handleCopyToken} edge="end">
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        }
                    />
                </Box>
                <Box mb={3}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        display="block"
                        mb={0.5}
                    >
                        Application ID
                    </Typography>
                    <OutlinedInput
                        fullWidth
                        readOnly
                        value={apiKey?.applicationId ?? ''}
                        size="small"
                        inputProps={monoInputProps}
                        endAdornment={
                            <InputAdornment position="end">
                                <Tooltip title="Copy application ID">
                                    <IconButton size="small" onClick={handleCopyApplicationId} edge="end">
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        }
                    />
                </Box>
                <Box display="flex" justifyContent="flex-end">
                    <Button variant="contained" onClick={onClose}>
                        Done
                    </Button>
                </Box>
            </Box>
        </MuiModal>
    );
};
